import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { withTax } from '../../taxUtil';
import { fmtDate } from '../../dateUtil';
import { formatCurrency } from '../../currencyUtil';
import { CONDITION_CLR, GradeBadge, SALE_CONDITION, WARRANTY } from '../../components/CarForm';

const labelFor = (options, value) => options.find(o => o.value === value)?.label || value;

const CONDITION_LABELS = new Set([
  'Tires', 'Brakes', 'AC', 'Engine', 'Transmission', 'Leaks', 'Doors/Locks', 'Exhaust', 'Windshield', 'Tint',
]);

function Lightbox({ photos, index, onClose, onPrev, onNext }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft'  && photos.length > 1) onPrev();
      if (e.key === 'ArrowRight' && photos.length > 1) onNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [photos.length, onClose, onPrev, onNext]);

  const url = photos[index];
  const isVideo = /\.(mp4|mov|webm|ogg)(\?|$)/i.test(url);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/92 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Close */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 text-white transition-colors z-10"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Media */}
      {isVideo ? (
        <video
          src={url}
          controls
          autoPlay
          className="max-w-full max-h-[85dvh] rounded-lg shadow-2xl"
          onClick={e => e.stopPropagation()}
        />
      ) : (
        <img
          src={url}
          alt=""
          draggable={false}
          className="max-w-full max-h-[85dvh] object-contain rounded-lg shadow-2xl select-none"
          onClick={e => e.stopPropagation()}
        />
      )}

      {/* Prev */}
      {photos.length > 1 && (
        <button
          onClick={e => { e.stopPropagation(); onPrev(); }}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 text-white transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      {/* Next */}
      {photos.length > 1 && (
        <button
          onClick={e => { e.stopPropagation(); onNext(); }}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/25 text-white transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}

      {/* Counter */}
      {photos.length > 1 && (
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/60 text-sm font-medium tabular-nums">
          {index + 1} / {photos.length}
        </div>
      )}
    </div>
  );
}

function NoPhoto({ onClick }) {
  return (
    <div
      onClick={onClick}
      className="w-full h-full flex flex-col items-center justify-center bg-slate-100 dark:bg-slate-700 text-slate-300 dark:text-slate-600"
    >
      <svg className="w-12 h-8 mb-1" viewBox="0 0 60 40" fill="none">
        <path d="M8 28 Q12 14 20 12 L40 12 Q48 14 52 28 L54 34 Q54 37 51 37 L9 37 Q6 37 6 34 Z" fill="currentColor" opacity="0.5" />
        <circle cx="17" cy="37" r="5" fill="currentColor" />
        <circle cx="43" cy="37" r="5" fill="currentColor" />
        <path d="M22 12 L26 5 L34 5 L38 12" stroke="currentColor" strokeWidth="1.5" fill="none" />
      </svg>
      <span className="text-xs font-medium">No photo</span>
    </div>
  );
}

// All of a car's photos, horizontally scrollable in the same top image slot — replaces
// the old "first photo up top, rest listed at the bottom" layout.
function PhotoStrip({ photos, onPhotoClick, onEmptyClick }) {
  if (!photos?.length) return <NoPhoto onClick={onEmptyClick} />;
  return (
    <div className="w-full h-full flex overflow-x-auto snap-x snap-mandatory scrollbar-hide">
      {photos.map((url, i) => (
        <img
          key={i}
          src={url}
          alt={`Photo ${i + 1} of ${photos.length}`}
          tabIndex={0}
          role="button"
          onClick={() => onPhotoClick(i)}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPhotoClick(i); } }}
          className="w-full h-full object-cover cursor-zoom-in shrink-0 snap-center"
        />
      ))}
    </div>
  );
}

function DataGrid({ car }) {
  const pairs = [
    ['VIN', car.vin], ['Year', car.year], ['Make', car.make], ['Model', car.model],
    ['Trim', car.trim], ['Mileage', car.mileage ? `${Number(car.mileage).toLocaleString()} mi` : null],
    ['Grade', car.grade], ['Operational', car.operational === false ? 'No — inoperable' : 'Yes'],
    ['Title Status', car.title_status], ['Stock #', car.stock_number],
    ['Sale Condition', labelFor(SALE_CONDITION, car.sale_condition)],
    ['Warranty', labelFor(WARRANTY, car.warranty)],
    ['Safety Exp', car.safety_exp?.slice(0, 7)], ['Reg Exp', car.reg_exp?.slice(0, 7)],
    ['Tires', car.tires], ['Brakes', car.brakes], ['AC', car.ac], ['Engine', car.engine],
    ['Transmission', car.transmission], ['Leaks', car.leaks], ['Doors/Locks', car.doors_locks],
    ['Exhaust', car.exhaust], ['Windshield', car.windshield], ['Tint', car.tint],
  ].filter(([, v]) => v);

  const notes = [
    ['Warning Lights', car.warning_lights],
    ['OBD Scan Notes', car.obd_scan_notes],
    ['Seller Disclosure', car.seller_disclosure],
    ['What Seller Owes', car.sale_condition === 'we_owe' ? car.we_owe_details : null],
  ].filter(([, v]) => v);

  const listingUrl = car.listing_url;
  const warrantyDocUrl = car.warranty === 'with_warranty' ? car.warranty_document_url : null;

  return (
    <div className="mt-5 pt-5 border-t border-slate-100 dark:border-slate-700 space-y-4">
      <div className="grid grid-cols-2 gap-x-6 gap-y-3 lg:grid-cols-4">
        {pairs.map(([label, value]) => (
          <div key={label}>
            <p className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wide">{label}</p>
            <p className={`text-sm mt-0.5 ${CONDITION_LABELS.has(label) ? (CONDITION_CLR[value] || 'text-slate-800 dark:text-slate-200') : 'text-slate-800 dark:text-slate-200'}`}>
              {value}
            </p>
          </div>
        ))}
      </div>
      {listingUrl && (
        <div>
          <a
            href={listingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 hover:underline"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
            View seller's listing
          </a>
        </div>
      )}
      {warrantyDocUrl && (
        <div>
          <a
            href={warrantyDocUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 hover:underline"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            View warranty document
          </a>
        </div>
      )}
      {notes.map(([label, value]) => (
        <div key={label}>
          <p className="text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wide">{label}</p>
          <p className="text-sm text-slate-700 dark:text-slate-300 mt-0.5 bg-slate-50 dark:bg-slate-700/50 rounded-lg p-2.5">{value}</p>
        </div>
      ))}
    </div>
  );
}

export default function DealView() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [deal, setDeal]         = useState(null);
  const [cars, setCars]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [bidAmounts, setBidAmounts] = useState({});
  const [submitting, setSubmitting] = useState({});
  const [submitted, setSubmitted]   = useState({});
  const [bidErrors, setBidErrors]       = useState({});
  const [buyAllError, setBuyAllError]   = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // 'all' | car object | null
  const [lightbox, setLightbox]         = useState(null); // { photos, index }
  const [myBids, setMyBids]             = useState([]);
  const [counterAmounts, setCounterAmounts] = useState({});
  const [responding, setResponding]         = useState({});
  const [outbidCarIds, setOutbidCarIds]     = useState(() => new Set());
  const prevTopBidRef = useRef({}); // car_id -> was this buyer's pending bid the top one, as of the last fetch
  const inFlightRef = useRef(new Set()); // synchronous re-entrancy guard, keyed by car.id / bid.id — closes the gap before disabled-state re-renders
  const buyNowTriggerRef = useRef(null); // element that opened the Buy Now modal, so focus can return to it on close

  // Merges freshly-fetched bids into state and flags cars where the buyer just lost the
  // lead since the last fetch (initial page load never flags — nothing to compare against).
  const applyMyBids = (bids, { detectOutbid } = {}) => {
    if (detectOutbid) {
      const newlyOutbid = (bids || []).filter(b =>
        b.status === 'pending' && b.is_top_bid === false && prevTopBidRef.current[b.car_id] === true
      );
      if (newlyOutbid.length) {
        setOutbidCarIds(prev => new Set([...prev, ...newlyOutbid.map(b => b.car_id)]));
      }
    }
    for (const b of bids || []) {
      if (b.status === 'pending') prevTopBidRef.current[b.car_id] = b.is_top_bid !== false;
    }
    setMyBids(bids || []);
  };

  useEffect(() => {
    let mounted = true;
    api.get(`/deals/${id}`)
      .then(({ cars: c, ...d }) => {
        if (!mounted) return;
        setDeal(d); setCars(c || []);
        api.get(`/bids?deal_id=${d.id}&mine=true`)
          .then(bids => { if (mounted) applyMyBids(bids); })
          .catch(() => {});
      })
      .catch(() => { if (mounted) setDeal(null); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [id]);

  // Poll for bid-status changes (competing bids, outbid status) while the deal is open —
  // this page has no push/live-update mechanism, so we settle for a short interval.
  useEffect(() => {
    if (!deal?.id) return;
    const interval = setInterval(() => {
      api.get(`/bids?deal_id=${deal.id}&mine=true`)
        .then(bids => applyMyBids(bids, { detectOutbid: true }))
        .catch(() => {});
    }, 12000);
    return () => clearInterval(interval);
  }, [deal?.id]);

  // Most recent bid per car (backend orders by car_id, created_at DESC)
  const myBidByCar = {};
  for (const b of myBids) {
    if (!(b.car_id in myBidByCar)) myBidByCar[b.car_id] = b;
  }

  const openLightbox  = (photos, index) => { if (photos?.length) setLightbox({ photos, index }); };
  const closeLightbox = () => setLightbox(null);
  const prevPhoto     = () => setLightbox(l => l ? { ...l, index: (l.index - 1 + l.photos.length) % l.photos.length } : l);
  const nextPhoto     = () => setLightbox(l => l ? { ...l, index: (l.index + 1) % l.photos.length } : l);

  const submitBid = async (car, type) => {
    const price = type === 'buy_now' ? car.buy_now_price : bidAmounts[car.id];
    if (!price) return false;
    if (inFlightRef.current.has(car.id)) return false;
    inFlightRef.current.add(car.id);
    setSubmitting(s => ({ ...s, [car.id]: true }));
    setBidErrors(e => ({ ...e, [car.id]: null }));
    try {
      const newBid = await api.post('/bids', {
        car_id: car.id,
        bid_price: price,
        bid_type: type,
      });
      setSubmitted(s => ({ ...s, [car.id]: type }));
      setMyBids(prev => [newBid, ...prev.filter(b => b.id !== newBid.id)]);
      setOutbidCarIds(prev => { if (!prev.has(car.id)) return prev; const next = new Set(prev); next.delete(car.id); return next; });
      return true;
    } catch (err) {
      setBidErrors(e => ({ ...e, [car.id]: err.message }));
      return false;
    } finally {
      setSubmitting(s => ({ ...s, [car.id]: false }));
      inFlightRef.current.delete(car.id);
    }
  };

  const respondToCounter = async (bid, status) => {
    setResponding(r => ({ ...r, [bid.id]: true }));
    setBidErrors(e => ({ ...e, [bid.car_id]: null }));
    try {
      const updated = await api.put(`/bids/${bid.id}`, { status });
      setMyBids(prev => prev.map(b => b.id === bid.id ? { ...b, ...updated } : b));
      if (status === 'accepted') {
        const { cars: c, ...d } = await api.get(`/deals/${id}`);
        setDeal(d); setCars(c || []);
      }
    } catch (err) {
      setBidErrors(e => ({ ...e, [bid.car_id]: err.message }));
    } finally {
      setResponding(r => ({ ...r, [bid.id]: false }));
    }
  };

  const submitCounter = async (bid) => {
    const price = counterAmounts[bid.id];
    if (!price) return;
    if (inFlightRef.current.has(bid.id)) return;
    inFlightRef.current.add(bid.id);
    setResponding(r => ({ ...r, [bid.id]: true }));
    setBidErrors(e => ({ ...e, [bid.car_id]: null }));
    try {
      const updated = await api.post(`/bids/${bid.id}/counter`, { price });
      setMyBids(prev => prev.map(b => b.id === bid.id ? { ...b, ...updated } : b));
      setCounterAmounts(a => ({ ...a, [bid.id]: '' }));
      setOutbidCarIds(prev => { if (!prev.has(bid.car_id)) return prev; const next = new Set(prev); next.delete(bid.car_id); return next; });
    } catch (err) {
      setBidErrors(e => ({ ...e, [bid.car_id]: err.message }));
    } finally {
      setResponding(r => ({ ...r, [bid.id]: false }));
      inFlightRef.current.delete(bid.id);
    }
  };

  const closeConfirmModal = () => {
    setConfirmAction(null);
    buyNowTriggerRef.current?.focus();
  };

  const confirmBuyNow = () => {
    if (confirmAction === 'all') { buyNowAll(); return; }
    const car = confirmAction;
    setConfirmAction(null);
    submitBid(car, 'buy_now');
  };

  // Escape-to-close for the Buy Now confirmation modal.
  useEffect(() => {
    if (!confirmAction) return;
    const onKey = (e) => { if (e.key === 'Escape') closeConfirmModal(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirmAction]);

  const buyNowAll = async () => {
    setConfirmAction(null);
    setBuyAllError(null);
    let failed = 0;
    for (const car of cars) {
      if (!submitted[car.id] && !car.has_accepted_bid && car.buy_now_price) {
        const ok = await submitBid(car, 'buy_now');
        if (!ok) failed++;
      }
    }
    if (failed > 0) {
      setBuyAllError(`${failed} bid${failed !== 1 ? 's' : ''} could not be submitted — see details below.`);
    }
  };

  const availableCars  = cars.filter(c => !c.has_accepted_bid);
  const anySubmitted   = availableCars.some(c => submitted[c.id]);
  // Bulk discount only applies while none of the deal's cars have been purchased —
  // mirrors the backend's TOTAL_PRICE_EXPR, applied on top of the per-car taxed price.
  const anyPurchased   = cars.some(c => c.has_accepted_bid);
  const discountPct    = anyPurchased ? 0 : Number(deal?.bulk_discount_percent || 0);
  const buyNowTotal    = availableCars.reduce((sum, c) => sum + withTax(c.buy_now_price, c.tax_rate), 0) * (1 - discountPct / 100);
  const buyNowTotalFmt = buyNowTotal ? `$${formatCurrency(buyNowTotal)}` : null;

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" /></div>;

  if (!deal) return (
    <div className="flex flex-col items-center justify-center h-full py-24 text-center px-6">
      <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center mb-4">
        <svg className="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <h2 className="text-base font-semibold text-slate-700 dark:text-slate-300 mb-1">Deal not found</h2>
      <p className="text-sm text-slate-400 dark:text-slate-500 mb-5">This deal doesn't exist or you don't have access to it.</p>
      <button onClick={() => navigate('/buyer/deals')} className="btn-primary text-sm px-4 py-2">Back to deals</button>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      {buyAllError && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm">
          {buyAllError}
        </div>
      )}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <button onClick={() => navigate('/buyer/deals')}
            className="text-sm text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 flex items-center gap-1 mb-2 transition-colors">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Available Deals
          </button>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{deal.name || `Deal #${deal.id}`}</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {deal.transaction_type} &middot; {deal.pickup_or_delivery}
            {deal.inspection_window ? ` · ${deal.inspection_window}-day inspection` : ''}
            {deal.end_date ? ` · Ends ${fmtDate(deal.end_date)}` : ''}
          </p>
          {deal.seller_location_name && (
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              {[deal.seller_account_name, deal.seller_location_name].filter(Boolean).join(' — ')}
              {deal.seller_address && ` · ${[deal.seller_address, deal.seller_zip].filter(Boolean).join(' ')}`}
            </p>
          )}
        </div>
        {!anySubmitted && availableCars.length > 0 && (
          <button onClick={(e) => { buyNowTriggerRef.current = e.currentTarget; setConfirmAction('all'); }} className="btn-primary flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Buy Now All{buyNowTotalFmt ? ` · ${buyNowTotalFmt}` : ''}
          </button>
        )}
      </div>

      <div className="space-y-3">
        {cars.map((car) => {
          const isExpanded = expandedId === car.id;
          const isDone     = !!submitted[car.id];
          const isSold     = !!car.has_accepted_bid;
          const carPhotos  = Array.isArray(car.photos_videos) ? car.photos_videos : [];
          const myBid      = myBidByCar[car.id];
          const sellerCountered = !isDone && !isSold && myBid?.status === 'pending' && myBid.last_offer_by === 'seller';
          const awaitingSeller  = !isDone && !isSold && myBid?.status === 'pending' && myBid.last_offer_by === 'buyer';
          const wasRejected     = !isDone && !isSold && myBid?.status === 'rejected';
          // Status-only visibility into competition — never amounts or identities (sealed-negotiation model).
          const showBidStatus   = !isSold && myBid?.status === 'pending' && myBid.bid_type === 'custom';
          const competingCount  = showBidStatus ? Number(myBid.competing_bids_count || 0) : 0;
          const isTopBid        = showBidStatus ? myBid.is_top_bid !== false : true;
          const justOutbid      = outbidCarIds.has(car.id);
          return (
            <div key={car.id} className={`card transition-all ${isExpanded ? 'ring-1 ring-blue-400 dark:ring-blue-600 border-blue-300 dark:border-blue-700' : ''} ${isDone || isSold ? 'opacity-60' : ''}`}>
              {justOutbid && showBidStatus && (
                <div className="flex items-center justify-between gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-semibold rounded-t-lg">
                  <span>You've been outbid on this car — another buyer placed a higher offer.</span>
                  <button
                    onClick={() => setOutbidCarIds(prev => { const next = new Set(prev); next.delete(car.id); return next; })}
                    className="text-amber-500 hover:text-amber-700 dark:hover:text-amber-300 shrink-0"
                  >
                    Dismiss
                  </button>
                </div>
              )}
              <div className="flex flex-col sm:flex-row sm:items-stretch">
                <div className="w-full h-40 sm:w-32 sm:h-24 shrink-0 overflow-hidden rounded-t-lg sm:rounded-tr-none sm:rounded-l-xl">
                  <PhotoStrip
                    photos={carPhotos}
                    onPhotoClick={(i) => openLightbox(carPhotos, i)}
                    onEmptyClick={() => setExpandedId(isExpanded ? null : car.id)}
                  />
                </div>

                <div className="flex-1 min-w-0 p-4 flex flex-wrap sm:flex-nowrap items-center gap-3 sm:gap-4">
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : car.id)}>
                    <p className="font-semibold text-slate-800 dark:text-slate-100 truncate">
                      {[car.year, car.make, car.model, car.trim].filter(Boolean).join(' ') || car.vin || `Car #${car.id}`}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                      {car.vin ? `VIN: ${car.vin}` : 'No VIN'} &middot; {car.mileage ? `${Number(car.mileage).toLocaleString()} mi` : '—'}
                    </p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <GradeBadge grade={car.grade} />
                      {car.operational === false && (
                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-600 ring-1 ring-red-200 dark:bg-red-900/30 dark:text-red-400 dark:ring-red-800">
                          Inoperable
                        </span>
                      )}
                    </div>
                    {car.buy_now_price && (
                      <p className="text-sm font-bold text-blue-600 dark:text-blue-400 mt-1">
                        Buy Now: ${formatCurrency(withTax(car.buy_now_price, car.tax_rate))}
                      </p>
                    )}
                  </div>

                  {isSold ? (
                    <div className="flex items-center gap-1.5 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 text-xs font-semibold px-3 py-1.5 rounded-full ring-1 ring-slate-200 dark:ring-slate-600 shrink-0">
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Sold
                    </div>
                  ) : isDone ? (
                    <div className="flex items-center gap-1.5 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs font-semibold px-3 py-1.5 rounded-full ring-1 ring-emerald-200 dark:ring-emerald-800 shrink-0">
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                      </svg>
                      {submitted[car.id] === 'buy_now' ? 'Buy Now Sent' : 'Bid Sent'}
                    </div>
                  ) : sellerCountered ? (
                    <div className="flex flex-col gap-1.5 items-stretch sm:items-end shrink-0 w-full sm:w-auto order-last sm:order-none">
                      <p className="text-xs font-semibold text-blue-600 dark:text-blue-400">
                        Seller countered: ${formatCurrency(myBid.bid_price)}
                      </p>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => respondToCounter(myBid, 'rejected')}
                          disabled={responding[myBid.id]}
                          className="flex-1 sm:flex-none px-3 py-2 sm:py-1.5 text-xs font-semibold text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-600 rounded-lg hover:border-red-300 hover:text-red-600 hover:bg-red-50 dark:hover:border-red-700 dark:hover:text-red-400 dark:hover:bg-red-900/20 transition-colors disabled:opacity-40"
                        >
                          Decline
                        </button>
                        <button
                          onClick={() => respondToCounter(myBid, 'accepted')}
                          disabled={responding[myBid.id]}
                          className="flex-1 sm:flex-none px-3 py-2 sm:py-1.5 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors disabled:opacity-40"
                        >
                          Accept
                        </button>
                      </div>
                      <div className="flex items-center border border-slate-200 dark:border-slate-600 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 dark:focus-within:ring-blue-400 transition-shadow">
                        <span className="pl-2.5 text-slate-400 text-xs">$</span>
                        <input
                          type="number"
                          min="1"
                          placeholder="Counter"
                          value={counterAmounts[myBid.id] || ''}
                          onChange={e => setCounterAmounts(a => ({ ...a, [myBid.id]: e.target.value }))}
                          className="flex-1 sm:flex-none sm:w-20 min-w-0 px-1.5 py-1.5 text-base sm:text-xs bg-transparent text-slate-800 dark:text-slate-200 focus:outline-none"
                        />
                        <button
                          onClick={() => submitCounter(myBid)}
                          disabled={!counterAmounts[myBid.id] || responding[myBid.id]}
                          className="px-2 py-1.5 text-xs font-semibold bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 border-l border-slate-200 dark:border-slate-600 transition-colors disabled:opacity-40"
                        >
                          Counter
                        </button>
                      </div>
                    </div>
                  ) : awaitingSeller ? (
                    <div className="flex flex-col gap-2 items-stretch sm:items-end shrink-0 w-full sm:w-auto order-last sm:order-none">
                      <div className="flex items-center gap-1.5 bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-semibold px-3 py-1.5 rounded-full ring-1 ring-amber-200 dark:ring-amber-800">
                        Bid pending: ${formatCurrency(myBid.bid_price)}
                      </div>
                      {showBidStatus && (
                        <p className={`text-xs font-medium ${isTopBid ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
                          {isTopBid
                            ? (competingCount > 0 ? `Leading · ${competingCount} other active bid${competingCount !== 1 ? 's' : ''}` : 'You\'re the only bidder')
                            : `Outbid · ${competingCount} other active bid${competingCount !== 1 ? 's' : ''}`}
                        </p>
                      )}
                      {car.buy_now_price && (
                        <button
                          onClick={(e) => { buyNowTriggerRef.current = e.currentTarget; setConfirmAction(car); }}
                          disabled={submitting[car.id]}
                          className="w-full px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold shadow-sm transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          Buy Now
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2 shrink-0 w-full sm:w-auto order-last sm:order-none">
                      {wasRejected && (
                        <div className="flex items-center gap-1.5 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-xs font-semibold px-3 py-1.5 rounded-full ring-1 ring-red-200 dark:ring-red-800">
                          Offer declined
                        </div>
                      )}
                      {car.bidding_enabled && !wasRejected && (
                        <div className="flex items-center border border-slate-200 dark:border-slate-600 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 dark:focus-within:ring-blue-400 transition-shadow">
                          <span className="pl-3 text-slate-400 text-sm">$</span>
                          <input
                            type="number"
                            min="1"
                            placeholder="Your bid"
                            value={bidAmounts[car.id] || ''}
                            onChange={e => setBidAmounts(b => ({ ...b, [car.id]: e.target.value }))}
                            className="flex-1 sm:flex-none sm:w-28 min-w-0 px-2 py-2 text-base sm:text-sm bg-transparent text-slate-800 dark:text-slate-200 focus:outline-none"
                          />
                          <button
                            onClick={() => submitBid(car, 'custom')}
                            disabled={!bidAmounts[car.id] || submitting[car.id]}
                            className="px-3 py-2 text-xs font-semibold bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 border-l border-slate-200 dark:border-slate-600 transition-colors disabled:opacity-40"
                          >
                            Bid
                          </button>
                        </div>
                      )}
                      {car.buy_now_price && (
                        <button
                          onClick={(e) => { buyNowTriggerRef.current = e.currentTarget; setConfirmAction(car); }}
                          disabled={submitting[car.id]}
                          className="w-full px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold shadow-sm transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          Buy Now
                        </button>
                      )}
                    </div>
                  )}

                  <button onClick={() => setExpandedId(isExpanded ? null : car.id)}
                    className="p-2 sm:p-1 -m-1 sm:m-0 text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 transition-colors">
                    <svg className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
                {bidErrors[car.id] && (
                  <p className="px-4 pb-3 text-xs text-red-600 dark:text-red-400">{bidErrors[car.id]}</p>
                )}
              </div>

              {isExpanded && (
                <div className="px-5 pb-5">
                  <DataGrid car={car} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {confirmAction && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={closeConfirmModal}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-buy-now-title"
            onClick={e => e.stopPropagation()}
            className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-sm p-6 max-h-[85dvh] overflow-y-auto"
          >
            <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/30 rounded-xl flex items-center justify-center mb-4">
              <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h2 id="confirm-buy-now-title" className="text-lg font-bold text-slate-900 dark:text-white mb-1">Confirm Buy Now</h2>
            {confirmAction === 'all' ? (
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
                Accept the Buy Now price for all <strong>{availableCars.length}</strong> available car{availableCars.length !== 1 ? 's' : ''} in this deal?
              </p>
            ) : (
              <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">
                Accept the Buy Now price of <strong>${formatCurrency(withTax(confirmAction.buy_now_price, confirmAction.tax_rate))}</strong> for{' '}
                <strong>{[confirmAction.year, confirmAction.make, confirmAction.model, confirmAction.trim].filter(Boolean).join(' ') || confirmAction.vin}</strong>?
              </p>
            )}
            <div className="flex gap-2">
              <button onClick={closeConfirmModal} className="btn-secondary flex-1">Cancel</button>
              <button onClick={confirmBuyNow} className="btn-primary flex-1">Confirm</button>
            </div>
          </div>
        </div>
      )}

      {lightbox && (
        <Lightbox
          photos={lightbox.photos}
          index={lightbox.index}
          onClose={closeLightbox}
          onPrev={prevPhoto}
          onNext={nextPhoto}
        />
      )}
    </div>
  );
}
